import { z } from 'zod'
import { pullIssueResponseSchema } from './issue-pull'
import { issueSourceSchema } from './report-issue'

/**
 * What a report would send right now, without sending it (CLI-625): the editor's pull and the
 * mailer's render run for an issue that is never committed — `previewReport` in
 * apps/api/src/lib/issue-preview.ts builds it with `persist: false`, so nothing is written. Served
 * to the editor (`GET /reports/:id/preview`, the session) and to engineering
 * (`GET /engineering/reports/:id/preview`, its own token) in one shape, so the parity tool reads
 * the same fields the editor's dialog shows. Recipients are counts and domains only, on both
 * routes: the engineering caller has no session, and the editor sees the addresses on the
 * Delivery tab already.
 */
export const reportPreviewResponseSchema = z.object({
  reportId: z.uuid(),
  name: z.string(),
  /**
   * The same build written as the file a send would deliver, when staff asked for one ("Write as
   * Google Doc / Sheet", CLI-925 / CLI-940) — next to the email it would send.
   */
  googleDocUrl: z.url().optional(),
  googleSheetUrl: z.url().optional(),
  /** When the pull ran — the preview's "as of". */
  pulledAt: z.iso.datetime(),
  /**
   * The coverage window the pull covered: New draft's default, ending at `pulledAt` — or, on a
   * replay, the window the caller named.
   */
  window: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  /**
   * True when the caller named the window (`previewReplayQuerySchema`): a replay of the send at
   * `window.to`, previously-sent judged as of `window.from` — not what would send right now.
   */
  replay: z.boolean(),
  /** The subject as the mailer would send it — legacy date tokens rendered in the report's timezone. */
  subject: z.string(),
  html: z.string(),
  template: z.enum(['standard', 'custom']),
  templateId: z.uuid().nullable(),
  from: z.object({ email: z.string(), name: z.string() }),
  recipients: z.object({
    to: z.number().int().nonnegative(),
    cc: z.number().int().nonnegative(),
    bcc: z.number().int().nonnegative(),
    domains: z.array(z.string()),
  }),
  pull: z.object({
    added: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: pullIssueResponseSchema.shape.failed,
    durationMs: z.number().int().nonnegative(),
  }),
  sections: z.array(
    z.object({
      /** The live header's id; null for a header the configuration no longer has. */
      sectionId: z.uuid().nullable(),
      name: z.string(),
      counts: z.array(
        z.object({ source: issueSourceSchema, count: z.number().int().nonnegative() }),
      ),
      /** The kept articles in issue order — what the email lists under this header. */
      articles: z.array(
        z.object({
          title: z.string().nullable(),
          url: z.string().nullable(),
          outlet: z.string().nullable(),
          hitId: z.string().nullable(),
          source: issueSourceSchema,
          date: z.string().nullable(),
          /** The Schmidt programme the data API attributed, which a Google Doc's subtabs route on. */
          program: z.string().nullable(),
        }),
      ),
    }),
  ),
})
export type ReportPreviewResponse = z.infer<typeof reportPreviewResponseSchema>
